import { UserAction } from "@brookbush/types";

import Image from "next/image";
import Link from "next/link";

import { useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import { CPT_PROGRAMS_PAGE } from "~/lib/cpt-programs-page";
import NccaAccreditedProgramMark from "~/pages/exam-portal/components/NccaAccreditedProgramMark";

/** Match NccaAccreditedProgramMark `md` (120px) so the two program marks align. */
const BI_SHIELD_PX = 120;

const BiShield = ({ className }: { className?: string }) => (
	<div
		className={
			className ??
			"relative shrink-0 overflow-hidden rounded-md bg-white shadow-sm"
		}
		style={{ width: BI_SHIELD_PX, height: BI_SHIELD_PX }}
	>
		<Image
			src={CPT_PROGRAMS_PAGE.biShieldSrc}
			alt="Brookbush Institute shield"
			width={BI_SHIELD_PX}
			height={BI_SHIELD_PX}
			className="h-full w-full object-contain p-1"
		/>
	</div>
);

const CptProgramsHero = () => {
	const trackCTA = useTrackCTA();
	const { setUserAction } = useUserAction();

	return (
		<section
			className="relative flex flex-col items-center justify-center px-4 pt-24 pb-12 xl:px-16 xl:pt-28 xl:pb-16"
			aria-label="Hero"
		>
			<div className="absolute inset-0 overflow-hidden brightness-[0.4]">
				<Image
					src="https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/homepage-cta-8szIwVX0TOm4hY87BGmfkfyZCuadsT.png"
					alt=""
					fill
					priority
					quality={50}
					className="object-cover"
					sizes="100vw"
				/>
			</div>

			<div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col gap-8">
				<p className="text-center font-semibold text-2xl text-white tracking-tight sm:text-3xl lg:text-4xl">
					{CPT_PROGRAMS_PAGE.heroLine}
				</p>

				<h1 className="text-center font-semibold text-white text-xl tracking-tight sm:text-2xl lg:text-3xl">
					{CPT_PROGRAMS_PAGE.h1}
				</h1>

				<p className="mx-auto max-w-3xl text-center text-base text-white sm:text-lg lg:text-xl">
					{CPT_PROGRAMS_PAGE.heroText}
				</p>

				<div className="flex flex-col gap-4 rounded-xl border border-white/20 bg-black/40 p-4 text-white backdrop-blur-md sm:p-6">
					<h2 className="font-semibold text-lg text-white sm:text-xl">
						Two programs.
					</h2>

					<div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-4">
						<NccaAccreditedProgramMark size="md" />
						<p className="text-sm text-white sm:text-base lg:text-lg">
							<strong className="text-white">
								CPT Certification exam — NCCA-accredited.
							</strong>{" "}
							A standalone, proctored exam of 110 multiple-choice questions,
							online with live remote proctoring or at a testing center. The
							credential employers ask for. Open to anyone; no course or
							membership required to sit for it.
						</p>
					</div>

					<div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-4">
						<BiShield />
						<p className="text-sm text-white sm:text-base lg:text-lg">
							<strong className="text-white">
								Advanced BI-CPT Certificate — included in membership.
							</strong>{" "}
							Our education-based credential: a self-paced, modular curriculum
							built on systematic reviews of the research, with small exams
							after each course and a credit system that tracks your progress.
							100s of 5-star Google reviews.
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-4 rounded-xl border border-white/20 bg-black/40 p-4 text-white backdrop-blur-md sm:p-6">
					<div className="flex flex-col gap-3 text-left">
						<h2 className="font-semibold text-lg text-white sm:text-xl">
							And the best way to study — all included in membership.
						</h2>
						<p className="text-sm text-white sm:text-base lg:text-lg">
							Work toward your Advanced BI-CPT Certificate, which covers more
							ground than the certification exam tests. Then take the CPT
							Certification Practice Exam — built to feel like the
							NCCA-accredited CPT Certification exam, with 100 questions drawn
							at random from a larger bank, so it never goes stale. Then let our
							new Exam Coach tell you exactly what to study, in your own Study
							Plan. Every course you complete earns credit toward your BI-CPT
							and SPC certificates while you prepare.
						</p>
					</div>
					<div className="flex flex-col gap-3">
						<button
							type="button"
							onClick={() => {
								trackCTA(UserAction.SIGN_UP, "CPT Programs Hero Membership");
								setUserAction(UserAction.SIGN_UP);
							}}
							className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-sky-700 px-5 py-2.5 text-base text-white shadow-xl hover:brightness-95 sm:text-lg"
						>
							{CPT_PROGRAMS_PAGE.membershipPriceLabel}
						</button>
						<Link
							href={CPT_PROGRAMS_PAGE.examPortalHref}
							className="group flex items-center justify-center gap-2 rounded-xl border-2 border-white/80 bg-white/10 px-5 py-2.5 text-base text-white backdrop-blur hover:bg-white/20 sm:text-lg"
						>
							{CPT_PROGRAMS_PAGE.examPortalCtaLabel}
						</Link>
					</div>
				</div>

				<p className="text-center text-sm text-white/90 sm:text-base">
					{CPT_PROGRAMS_PAGE.trustLine}
				</p>
			</div>
		</section>
	);
};

export default CptProgramsHero;
